import clsx from 'clsx'
import { useRouter } from 'expo-router'
import { useCallback, useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { ActionSheet } from '@/src/elements/action'
import { Avatar } from '@/src/elements/avatar'
import { Button } from '@/src/elements/button/default'
import { Card, CardBody, CardFooter, CardHeader } from '@/src/elements/card'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { UserProfilePreview } from '@/src/elements/user-profile/UserProfilePreview/UserProfilePreview'
import { UserProfileWithModal } from '@/src/elements/user-profile/UserProfilePreviewWithModal/UserProfileWithModal'
import { ABUSE_TYPE } from '@/src/features/abuse/models/Abuse.enums'
import { ReportAbuseModal } from '@/src/features/abuse/ui/ReportAbuseModal/ReportAbuseModal'
import type { TCommentApi } from '@/src/features/comment/api/types/commentApi.types'
import { LinkifyText } from '@/src/general/components/LinkifyText'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { getAvatarUrl } from '@/src/general/utils/getAvatarUrl'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { useDeleteCommentMutation } from '../../services/useDeleteCommentMutation'
import { useFormattedDate } from './useFormattedDate'

export type TCommentProps = {
  comment: Omit<TCommentApi, 'responses'>
  onCommentReported?: VoidFunction
  isMine?: boolean
  className?: string
  isHozanaAdmin?: boolean
  parentId?: number
  publicationId: number
}

export const Comment = ({
  comment,
  isMine,
  parentId,
  isHozanaAdmin = false,
  className,
  onCommentReported,
  publicationId,
}: TCommentProps): ReactNode => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { t } = useTranslate()

  const { formattedTime, formattedDate, isToday } = useFormattedDate(comment.createdAt)

  const isResponse = !!parentId
  const canDeleteComment = isMine || isHozanaAdmin
  const { mutateAsync: onCommentDelete } = useDeleteCommentMutation(comment.id, publicationId)

  const router = useRouter()
  const { routes } = useRoutes()

  const onPrayButtonPress = useCallback(
    (commentToPray: TCommentApi) => {
      if (isResponse) {
        router.navigate(
          routes.publicationCommentPray({
            publicationId: publicationId,
            commentId: parentId,
            commentAuthor: commentToPray.user.name,
            responseId: commentToPray.id,
          }),
        )
      } else {
        router.navigate(
          routes.publicationCommentPray({
            publicationId: publicationId,
            commentId: commentToPray.id,
            commentAuthor: commentToPray.user.name,
          }),
        )
      }
    },
    [isResponse, parentId, publicationId, router, routes],
  )

  const commentDeleteButton = {
    onPress: () => onCommentDelete(),
    children: t('comment.delete.delete'),
    startContent: <Icon name={ICON.TRASH} size="sm" className="text-danger-text-contrast-default" />,
    testID: TEST_ID.COMMENT.OPTIONS.DELETE,
    color: 'danger',
  }

  const commentReportButton = {
    onPress: () => setIsModalVisible(true),
    children: t('app.report-abuse.comment'),
    startContent: <Icon name={ICON.ALERT_TRIANGLE} size="sm" />,
    testID: TEST_ID.COMMENT.OPTIONS.REPORT_ABUSE as string,
  }

  const options = onCommentReported ? [commentReportButton] : []

  return (
    <View className={clsx('flex flex-row gap-2', className)}>
      <Avatar src={getAvatarUrl(comment.user.avatar)} testID={TEST_ID.MISCELLANEOUS.AVATAR} size="xs" />
      <Card
        className="flex-1 overflow-visible border border-layout-stroke-contrast-medium bg-default-bg-white"
        shadow="none"
        radius="xl"
      >
        <CardHeader className="flex h-10 flex-row items-baseline justify-between p-1 pl-4 pr-2">
          {!isMine && (
            <UserProfileWithModal user={comment.user}>
              <UserProfilePreview.Text user={comment.user} className="text-sm" />
            </UserProfileWithModal>
          )}
          {!!isMine && (
            <Typography bold numberOfLines={1} className="text-default-text-contrast-high" size="sm">
              <FormattedMessage id="word.you" />
            </Typography>
          )}
          <View className="flex flex-row items-center">
            <Typography size="sm" className="px-2 text-default-text-contrast-low">
              {isToday ? formattedTime : formattedDate}
            </Typography>

            <ActionSheet items={canDeleteComment ? [...options, commentDeleteButton] : options}>
              <Button
                size="xs"
                isDisabled={!onCommentReported && !canDeleteComment}
                className={clsx({ 'opacity-15': !onCommentReported && !canDeleteComment })}
                isIconOnly
                startIcon={ICON.ELLIPSIS_VERTICAL}
                colors="default"
                variant="light"
                contrast="low"
                testID={TEST_ID.COMMENT.OPTIONS.BUTTON(comment.id)}
              />
            </ActionSheet>
          </View>
        </CardHeader>
        <CardBody className="overflow-visible px-4 pb-2 pt-0">
          <Typography className="text-default-text-contrast-high">
            <LinkifyText text={comment.content} />
          </Typography>
        </CardBody>
        {!isMine && (
          <CardFooter className="flex flex-row overflow-visible p-1">
            {comment.hasPrayed ? (
              <Button
                size="sm"
                colors="success"
                variant="faded"
                startIcon={ICON.FIRE}
                testID={TEST_ID.COMMENT.PRAYED_BUTTON(comment.id)}
                onPress={() => onPrayButtonPress(comment)}
              >
                {t('pray.prayed')}
                {comment.prayersCount > 0 && ` (${comment.prayersCount})`}
              </Button>
            ) : (
              <Button
                testID={TEST_ID.COMMENT.PRAY_BUTTON(comment.id)}
                size="sm"
                colors="default"
                variant="light"
                startIcon={ICON.FIRE}
                onPress={() => onPrayButtonPress(comment)}
              >
                {t('pray.i-pray.short')}
                {comment.prayersCount > 0 && ` (${comment.prayersCount})`}
              </Button>
            )}
          </CardFooter>
        )}
      </Card>
      {onCommentReported && (
        <ReportAbuseModal
          showModal={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          onDone={onCommentReported}
          objectId={comment.id}
          objectType={ABUSE_TYPE.COMMENT}
          text={comment.content}
        />
      )}
    </View>
  )
}
